import { type PoCreateRunErrors, type PoRun, poCreateRun } from '@repo/sdk/gen'
import { zPoListRunsData } from '@repo/sdk/zod'
import { HoverBorderGradient } from '@repo/ui/aceternity/hover-border-gradient'
import { StatefulButton } from '@repo/ui/aceternity/stateful-button'
import { Button } from '@repo/ui/button'
import { Checkbox } from '@repo/ui/checkbox'
import {
	ComboboxContent,
	ComboboxInput,
	ComboboxTrigger,
	useComboboxAnchor
} from '@repo/ui/combobox'
import {
	AsyncPickerList,
	AsyncPickerRoot,
	AsyncPickerSelectionActions
} from '@repo/ui/commons/async-pickers'
import {
	DataTable,
	useDataTableColumnState
} from '@repo/ui/commons/data-table/data-table'
import type { DataTableColumn } from '@repo/ui/commons/data-table/types'
import { FilterGroup, FiltersBar } from '@repo/ui/commons/filters-bar'
import { IdCell } from '@repo/ui/commons/id-cell'
import { InlineFilterAsyncSelect } from '@repo/ui/commons/inline-filters/inline-filter-async-options'
import { ListInputSearch } from '@repo/ui/commons/list-input-search'
import { ListLayout } from '@repo/ui/commons/list-layout'
import { formatLocaleDate } from '@repo/ui/dates'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle
} from '@repo/ui/dialog'
import {
	Field,
	FieldContent,
	FieldDescription,
	FieldGroup,
	FieldLabel
} from '@repo/ui/field'
import { Progress } from '@repo/ui/progress'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue
} from '@repo/ui/select'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@repo/ui/tabs'
import { getErrorCode } from '@repo/ui/utils'
import {
	useMutation,
	useQuery,
	useQueryClient,
	useSuspenseQuery
} from '@tanstack/react-query'
import {
	createFileRoute,
	Link,
	stripSearchParams,
	useNavigate
} from '@tanstack/react-router'
import { FilePlus2, FileText } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import { z } from 'zod'
import { pageTitle, type TKey, translateApp, useTranslation } from '@/lib/i18n'
import { makeQueryKeys } from '@/lib/query-keys'
import { DatasetNote } from '@/routes/(private)/documents/-components/dataset-notices'
import { MrBookConnection } from '@/routes/(private)/documents/-components/mrbook-connection'
import {
	RUN_STATUSES,
	RunStatusBadge
} from '@/routes/(private)/documents/-components/run-status-badge'
import { poErrorMessage } from '@/routes/(private)/documents/-lib/error-message'
import {
	distributorsQueryOptions,
	generationDefaultsQueryOptions,
	runsListQueryOptions,
	systemStatusQueryOptions
} from '@/routes/(private)/documents/-lib/query-options'
import { usePageSize } from '@/stores/use-page-size'

const EMPTY_RUNS: PoRun[] = []
// The operator's language is how often he places orders, never a days-of-cover
// window ("copertura, tu che intendi?" — demo 2026-07-16). The hint spells out
// what that buys: the horizon PLUS the supplier lead time, which is the period
// he actually reasons with ("dipende dagli editori, minimo 10 giorni").
const HORIZON_PRESETS = [
	{ days: 1, labelKey: 'po:index.horizon_daily' },
	{ days: 7, labelKey: 'po:index.horizon_weekly' },
	{ days: 14, labelKey: 'po:index.horizon_biweekly' },
	{ days: 30, labelKey: 'po:index.horizon_monthly' }
] as const
const RUN_STATUS = [
	'pending',
	'running',
	'in_progress',
	'completed',
	'archived',
	'failed'
] as const
// The backend's texts for a generation refused on the data speak of agents and
// sync rounds, and tell to refresh even when only the support can bring a table
// back: the expert reads what the dialog's note says about the same gaps.
const DATASET_REFUSALS = {
	no_complete_dataset: 'po:index.no_complete_dataset',
	dataset_not_ready: 'po:index.dataset_not_ready'
} satisfies Record<PoCreateRunErrors[422]['code'], TKey>
// The fields the document list can sort on, as the backend declares them.
const RUN_SORT_FIELDS = zPoListRunsData.shape.body.shape.sort
	.unwrap()
	.shape.by.unwrap().enum
// The name search as the backend accepts it: a longer one is refused.
const RUN_SEARCH = zPoListRunsData.shape.body.shape.filters
	.unwrap()
	.shape.search.unwrap()

// A value the URL cannot hold (an old link, a hand-edited address) falls back
// to its default instead of failing the page.
const searchSchema = z.object({
	search: RUN_SEARCH.trim().default('').catch(''),
	status: z.enum(RUN_STATUS).optional().catch(undefined),
	by: z.enum(RUN_SORT_FIELDS).optional().catch(undefined),
	direction: z.enum(['asc', 'desc']).optional().catch(undefined),
	page: z.number().int().min(1).default(1).catch(1)
})

export const Route = createFileRoute('/(private)/')({
	component: PoDocuments,
	validateSearch: searchSchema,
	search: {
		middlewares: [stripSearchParams(searchSchema.parse({}))]
	},
	// The new-document dialog states the covered-until date, which includes the
	// configured supplier lead time: warm the config so the date is never a
	// placeholder. The date of the data it will read is only warmed, never
	// awaited: a failing status must not take the documents list down.
	loader: ({ context }) => {
		context.queryClient.prefetchQuery(systemStatusQueryOptions())
		return context.queryClient.ensureQueryData(
			generationDefaultsQueryOptions()
		)
	},
	head: () => ({
		meta: [{ title: pageTitle(translateApp('it', 'po:page_title')) }]
	})
})

function PoDocuments() {
	const { t } = useTranslation()
	const navigate = useNavigate({ from: Route.fullPath })
	const params = Route.useSearch()
	const queryClient = useQueryClient()
	const pageSize = usePageSize((s) => s.pageSize)
	const setPageSize = usePageSize((s) => s.setPageSize)

	const query = useQuery(
		runsListQueryOptions({
			page: params.page,
			limit: pageSize,
			filters: {
				search: params.search || undefined,
				status: params.status ? [params.status] : undefined
			},
			sort: params.by
				? { by: params.by, direction: params.direction ?? 'asc' }
				: undefined
		})
	)

	const [createOpen, setCreateOpen] = useState(false)

	const createRun = useMutation({
		mutationFn: (data: {
			mode: 'campagna' | 'riordino'
			horizonDays?: number
			zeroStock?: boolean
			distributorNames?: string[]
		}) => poCreateRun({ body: { data } }),
		onSuccess: async () => {
			await queryClient.invalidateQueries({
				queryKey: makeQueryKeys.po.runsAll()
			})
			toast.success(t('po:index.generation_started'))
		},
		// Refused for the data: the dialog's note re-reads the status, so it
		// names the same gaps the refusal is about.
		onError: (error) => {
			const code = getErrorCode(error)
			if (
				code === 'no_complete_dataset' ||
				code === 'dataset_not_ready'
			) {
				queryClient.invalidateQueries({
					queryKey: makeQueryKeys.po.system()
				})
				toast.error(t(DATASET_REFUSALS[code]))
				return
			}
			// Another generation is running (maybe started elsewhere), or this
			// document was created and failed at once on an unreachable
			// queue: either way the list gets a row to show.
			queryClient.invalidateQueries({
				queryKey: makeQueryKeys.po.runsAll()
			})
			toast.error(poErrorMessage(error))
		}
	})

	const columns: DataTableColumn<PoRun, keyof typeof RUN_SORT_FIELDS>[] = [
		{
			id: 'id',
			header: t('po:index.columns.id'),
			pinned: 'left',
			cell: (dataItem) => <IdCell id={dataItem.id} />,
			minWidth: 240,
			flex: 1
		},
		{
			id: 'name',
			header: t('po:index.columns.name'),
			pinned: 'left',
			sortKey: RUN_SORT_FIELDS.name,
			cell: (dataItem) => (
				<Link
					className='truncate font-medium hover:underline'
					params={{ runId: dataItem.id }}
					to='/documents/$runId'
				>
					{dataItem.name}
				</Link>
			),
			minWidth: 260,
			flex: 2
		},
		{
			id: 'status',
			header: t('po:index.columns.status'),
			sortKey: RUN_SORT_FIELDS.status,
			cell: (dataItem) => <RunStatusBadge status={dataItem.status} />,
			minWidth: 130,
			flex: 1
		},
		{
			id: 'campaign',
			header: t('po:index.columns.campaign'),
			sortKey: RUN_SORT_FIELDS.campaign,
			cell: (dataItem) => dataItem.campaign,
			minWidth: 90,
			flex: 1
		},
		{
			id: 'progress',
			header: t('po:index.columns.review_progress'),
			cell: (dataItem) =>
				dataItem.totalRows > 0 ? (
					<div className='flex w-full items-center gap-2'>
						<Progress
							className='h-1.5 w-24 [&_[data-slot=progress-indicator]]:bg-emerald-500 [&_[data-slot=progress-track]]:bg-foreground/8'
							value={
								(dataItem.reviewedRows / dataItem.totalRows) *
								100
							}
						/>
						<span className='text-muted-foreground text-xs'>
							{t('po:index.reviewed_of', {
								reviewed: dataItem.reviewedRows,
								total: dataItem.totalRows
							})}
						</span>
					</div>
				) : (
					'-'
				),
			minWidth: 220,
			flex: 2
		},
		{
			id: 'createdAt',
			header: t('po:index.columns.created_at'),
			sortKey: RUN_SORT_FIELDS.createdAt,
			cell: (dataItem) => formatLocaleDate(dataItem.createdAt, 'P HH:mm'),
			minWidth: 150,
			flex: 1
		},
		{
			id: 'updatedAt',
			header: t('po:index.columns.updated_at'),
			description: t('po:index.columns.updated_at_hint'),
			sortKey: RUN_SORT_FIELDS.updatedAt,
			cell: (dataItem) => formatLocaleDate(dataItem.updatedAt, 'P HH:mm'),
			minWidth: 150,
			flex: 1
		}
	]

	const columnsState = useDataTableColumnState(columns)

	return (
		<ListLayout
			controlsSlot={
				<FiltersBar
					active={
						params.status
							? [
									{
										key: 'status',
										label: `${t('po:index.columns.status')}: ${t(RUN_STATUSES[params.status].label)}`,
										onRemove: () => {
											navigate({
												search: (prev) => ({
													...prev,
													status: undefined,
													page: 1
												}),
												replace: true
											})
										}
									}
								]
							: []
					}
					onReset={() => {
						navigate({
							search: (prev) => ({
								...prev,
								status: undefined,
								page: 1
							}),
							replace: true
						})
					}}
					search={
						<ListInputSearch
							maxLength={RUN_SEARCH.maxLength ?? undefined}
							placeholder={t('po:index.search_placeholder')}
							resetKeys={['page']}
						/>
					}
				>
					<FilterGroup label={t('shared:filters.workflow')}>
						<InlineFilterAsyncSelect
							label={t('po:index.columns.status')}
							onValueChange={(status) => {
								navigate({
									search: (prev) => ({
										...prev,
										status: status ?? undefined,
										page: 1
									}),
									replace: true
								})
							}}
							source={{
								items: RUN_STATUS,
								getKey: (status) => status,
								getLabel: (status) =>
									t(RUN_STATUSES[status].label),
								placeholder: t('po:index.all_statuses')
							}}
							value={params.status ?? null}
						/>
					</FilterGroup>
				</FiltersBar>
			}
			headEndSlot={
				<>
					<HoverBorderGradient
						disabled={createRun.isPending}
						onClick={() => setCreateOpen(true)}
						type='button'
					>
						<FilePlus2 />
						{t('po:index.new_document')}
					</HoverBorderGradient>
					<NewDocumentDialog
						onGenerate={async (data) =>
							(await createRun.mutateAsync(data)).data.id
						}
						// The dialog stays up through the button's spinner and check,
						// so a refused generation keeps the form and its choices.
						onGenerated={(runId) =>
							navigate({
								to: '/documents/$runId',
								params: { runId }
							})
						}
						onOpenChange={setCreateOpen}
						open={createOpen}
					/>
				</>
			}
			listSlot={
				<DataTable
					columns={columns}
					columnsState={columnsState}
					data={query.data?.data ?? EMPTY_RUNS}
					onRowClick={(row) =>
						navigate({
							to: '/documents/$runId',
							params: { runId: row.id }
						})
					}
					pagination={{
						pageIndex: params.page - 1,
						pageCount: query.data?.meta.totalPages ?? 1,
						onPageChange: (next) =>
							navigate({
								search: (prev) => ({ ...prev, page: next + 1 }),
								replace: true
							}),
						pageSize,
						onPageSizeChange: (next) => {
							setPageSize(next)
							navigate({
								search: (prev) => ({ ...prev, page: 1 }),
								replace: true
							})
						}
					}}
					sort={{
						state: { by: params.by, direction: params.direction },
						onSortChange: (next) =>
							navigate({
								search: (prev) => ({
									...prev,
									by: next.by,
									direction: next.direction,
									page: 1
								}),
								replace: true
							})
					}}
					state={{
						loading: query.isPending,
						error: query.isError,
						empty: {
							icon: <FileText />,
							title: t('po:index.empty_title'),
							description: t('po:index.empty_description'),
							action: (
								<Button
									disabled={createRun.isPending}
									onClick={() => setCreateOpen(true)}
								>
									<FilePlus2 />
									{t('po:index.new_document')}
								</Button>
							)
						}
					}}
				/>
			}
			summarySlot={<MrBookConnection />}
			title={t('po:page_title')}
		/>
	)
}

// One screen, not a wizard: document type, its period, the optional
// distributor restriction and the simulation flag, then a single primary
// action. The reorder period is named the way the client names it — how often
// he places orders — and the description states what that buys him, delivery
// days included ("dipende dagli editori, minimo 10 giorni" — demo 2026-07-16).
function NewDocumentDialog(props: {
	open: boolean
	onOpenChange: (open: boolean) => void
	onGenerate: (data: {
		mode: 'campagna' | 'riordino'
		horizonDays?: number
		zeroStock?: boolean
		distributorNames?: string[]
	}) => Promise<string>
	onGenerated: (runId: string) => void
}) {
	return (
		<Dialog onOpenChange={props.onOpenChange} open={props.open}>
			{/* No `open ?` guard: the popup unmounts on its own once the close
			    animation ends, so the form remounts fresh per open anyway.
			    Unmounting it on `open` flipping left an empty popup for the
			    length of the exit transition. */}
			<DialogContent className='sm:max-w-lg'>
				<NewDocumentForm
					onCancel={() => props.onOpenChange(false)}
					onGenerate={props.onGenerate}
					onGenerated={props.onGenerated}
				/>
			</DialogContent>
		</Dialog>
	)
}

// Mounted per open so every reopen starts from a clean draft.
function NewDocumentForm(props: {
	onCancel: () => void
	onGenerate: (data: {
		mode: 'campagna' | 'riordino'
		horizonDays?: number
		zeroStock?: boolean
		distributorNames?: string[]
	}) => Promise<string>
	onGenerated: (runId: string) => void
}) {
	const { t } = useTranslation()
	const tuning = useSuspenseQuery(generationDefaultsQueryOptions()).data
	const dataset = useQuery(systemStatusQueryOptions()).data?.dataset
	const [mode, setMode] = useState<'campagna' | 'riordino'>('riordino')
	const [horizonDays, setHorizonDays] = useState(tuning.reorderHorizonDays)
	const [zeroStock, setZeroStock] = useState(false)
	const [distributors, setDistributors] = useState<Set<string>>(new Set())
	// An order placed today only reaches the shelf after the supplier lead
	// time, so the document covers the horizon plus that wait — exactly how the
	// engine sizes it (reorderHorizonDays + leadTimeDays).
	const leadTimeDays = tuning.leadTimeDays
	const horizons = HORIZON_PRESETS.map((preset) => ({
		days: preset.days,
		label: t(preset.labelKey)
	}))
	// Configuration accepts any whole number of days, not just these presets.
	const horizonOptions = horizons.some(
		(preset) => preset.days === tuning.reorderHorizonDays
	)
		? horizons
		: [
				{
					days: tuning.reorderHorizonDays,
					label: t('po:index.horizon_custom', {
						days: tuning.reorderHorizonDays
					})
				},
				...horizons
			]

	return (
		<>
			<DialogHeader>
				<DialogTitle>{t('po:index.new_document')}</DialogTitle>
				<DialogDescription>
					{t('po:index.new_document_explainer')}
				</DialogDescription>
			</DialogHeader>

			<FieldGroup>
				<Tabs
					onValueChange={(value) => {
						if (value === 'riordino' || value === 'campagna') {
							setMode(value)
						}
					}}
					value={mode}
				>
					<TabsList className='w-full'>
						<TabsTrigger value='riordino'>
							{t('po:index.mode_riordino')}
						</TabsTrigger>
						<TabsTrigger value='campagna'>
							{t('po:index.mode_campagna')}
						</TabsTrigger>
					</TabsList>
					<TabsContent className='pt-4' value='riordino'>
						<Field>
							<FieldLabel htmlFor='reorder-horizon'>
								{t('po:index.reorder_group')}
							</FieldLabel>
							<Select
								items={Object.fromEntries(
									horizonOptions.map((preset) => [
										String(preset.days),
										preset.label
									])
								)}
								onValueChange={(value) =>
									setHorizonDays(Number(value))
								}
								value={String(horizonDays)}
							>
								<SelectTrigger id='reorder-horizon'>
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									{horizonOptions.map((preset) => (
										<SelectItem
											key={preset.days}
											value={String(preset.days)}
										>
											{preset.label}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
							<FieldDescription>
								{t('po:index.reorder_note', {
									lead: leadTimeDays,
									date: coveredUntil(
										horizonDays + leadTimeDays
									)
								})}
							</FieldDescription>
						</Field>
					</TabsContent>
					<TabsContent className='pt-4' value='campagna'>
						<FieldDescription>
							{t('po:index.mode_campagna_hint')}
						</FieldDescription>
					</TabsContent>
				</Tabs>

				<Field>
					<FieldLabel htmlFor='distributors'>
						{t('po:index.distributors_title')}
					</FieldLabel>
					<DistributorPicker
						onChange={setDistributors}
						selected={distributors}
					/>
				</Field>

				{/* Test tool only: staging and PR previews build with --mode staging. */}
				{(import.meta.env.DEV ||
					import.meta.env.MODE === 'staging') && (
					<Field orientation='horizontal'>
						<Checkbox
							checked={zeroStock}
							id='zero-stock-simulation'
							onCheckedChange={(checked) =>
								setZeroStock(checked === true)
							}
						/>
						<FieldContent>
							<FieldLabel htmlFor='zero-stock-simulation'>
								{t('po:index.simulation_label')}
							</FieldLabel>
							<FieldDescription>
								{t('po:index.simulation_hint')}
							</FieldDescription>
						</FieldContent>
					</Field>
				)}
			</FieldGroup>

			<DatasetNote />

			<DialogFooter>
				<Button onClick={props.onCancel} variant='outline'>
					{t('common:cancel')}
				</Button>
				<StatefulButton
					disabled={
						dataset !== undefined &&
						(dataset.asOf === null ||
							dataset.missingRequired.length > 0)
					}
					onClick={() =>
						props.onGenerate({
							mode,
							horizonDays:
								mode === 'riordino' ? horizonDays : undefined,
							zeroStock,
							distributorNames:
								distributors.size > 0
									? [...distributors]
									: undefined
						})
					}
					onDone={props.onGenerated}
				>
					{t('po:index.generate_document')}
				</StatefulButton>
			</DialogFooter>
		</>
	)
}

// Empty selection means the whole catalog; the shared picker owns search and
// selection so bulk actions apply to the same options the operator can see.
function DistributorPicker(props: {
	selected: Set<string>
	onChange: (selected: Set<string>) => void
}) {
	const { t } = useTranslation()
	const anchor = useComboboxAnchor()
	const distributorsQuery = useQuery(distributorsQueryOptions())

	return (
		<AsyncPickerRoot
			multiple
			onValueChange={(names) => {
				props.onChange(new Set(names))
			}}
			searchInputValue
			source={{
				items: (distributorsQuery.data ?? []).map(
					(distributor) => distributor.name
				),
				isPending: distributorsQuery.isPending,
				isError: distributorsQuery.isError,
				getKey: (name) => name,
				getLabel: (name) => name,
				placeholder: t('po:index.distributors_search_placeholder'),
				empty: t('po:index.distributors_no_match')
			}}
			value={[...props.selected]}
		>
			<div ref={anchor}>
				<ComboboxTrigger
					aria-label={t('po:index.distributors_title')}
					id='distributors'
					render={
						<Button
							className='w-full justify-between font-normal'
							variant='outline'
						/>
					}
				>
					<span className='min-w-0 truncate'>
						{props.selected.size === 0
							? t('po:index.distributors_all')
							: t('po:index.distributors_selected_count', {
									count: props.selected.size
								})}
					</span>
				</ComboboxTrigger>
			</div>
			<ComboboxContent
				anchor={anchor}
				className='flex min-w-(--anchor-width) flex-col [&>[data-slot=input-group]]:shrink-0'
			>
				<ComboboxInput
					aria-label={t('po:index.distributors_title')}
					placeholder={t('po:index.distributors_search_placeholder')}
					showTrigger={false}
				/>
				<AsyncPickerSelectionActions
					clearLabel={t('po:index.distributors_clear_all')}
					selectAllLabel={t('po:index.distributors_select_all')}
				/>
				<AsyncPickerList />
			</ComboboxContent>
		</AsyncPickerRoot>
	)
}

// "Covered until <date>": the concrete meaning of a reorder horizon.
function coveredUntil(days: number): string {
	return formatLocaleDate(new Date(Date.now() + days * 86_400_000), 'd MMMM')
}
